/**
 * Sous-routes de `/reglages` (lot Simplicité S10) — une par section.
 *
 * Chaque sous-page passe par `SettingsSectionGuard` : un rôle sans droit est
 * renvoyé sur `/reglages`, même par un lien direct ou un favori.
 */

import type { ComponentType } from "react";
import { Navigate, type RouteObject } from "react-router-dom";

import { SettingsSectionGuard } from "@/features/settings/settings-guard";
import type { SettingsSlug } from "@/features/settings/settings-nav";
import { importWithStaleChunkReload } from "@/lib/stale-chunk";

function section<K extends PropertyKey, T extends Record<K, ComponentType>>(
  slug: SettingsSlug,
  load: () => Promise<T>,
  exportName: K,
): RouteObject {
  return {
    path: slug,
    lazy: async () => {
      const module = await importWithStaleChunkReload(load);
      const Page: ComponentType = module[exportName];
      return {
        Component: () => (
          <SettingsSectionGuard slug={slug}>
            <Page />
          </SettingsSectionGuard>
        ),
      };
    },
  };
}

const simple = () => import("@/features/settings/sections/simple-sections");

export const settingsRoutes: RouteObject[] = [
  {
    index: true,
    lazy: async () => ({
      Component: (await import("@/features/settings/settings-index")).SettingsIndexPage,
    }),
  },
  section(
    "profil",
    () => import("@/features/settings/sections/profile-section"),
    "ProfileSettingsPage",
  ),
  section(
    "securite",
    () => import("@/features/settings/sections/security-section"),
    "SecuritySettingsPage",
  ),
  section("bibliotheque", simple, "LibrarySettingsPage"),
  section(
    "cabinet",
    () => import("@/features/settings/sections/cabinet-section"),
    "CabinetSettingsPage",
  ),
  section(
    "identite-reglementaire",
    () => import("@/features/settings/sections/regulatory-identity-section"),
    "RegulatoryIdentitySettingsPage",
  ),
  section("equipe", () => import("@/features/settings/sections/team-section"), "TeamSettingsPage"),
  section("modeles", simple, "TemplatesSettingsPage"),
  section(
    "apparence-documents",
    () => import("@/features/settings/sections/document-branding-section"),
    "DocumentBrandingSettingsPage",
  ),
  section(
    "automatisations",
    () => import("@/features/settings/sections/automations-section"),
    "AutomationsSettingsPage",
  ),
  section(
    "modules-ia",
    () => import("@/features/settings/sections/modules-ai-section"),
    "ModulesAiSettingsPage",
  ),
  section("consommation", simple, "UsageSettingsPage"),
  section("journal", simple, "AuditSettingsPage"),
  { path: "*", element: <Navigate to="/reglages" replace /> },
];
